import type React from "react";

import { Stack } from "@mui/material";

import { SkeletonLayout } from "../SkeletonLayout";

interface GraphSkeletonProps {
  barCount?: number;
  height?: number;
}

export const GraphSkeleton: React.FC<GraphSkeletonProps> = ({ barCount = 6, height = 220 }) => {
  const barHeight = height - 50; // Account for x-axis labels

  return (
    <Stack sx={{ width: "100%", height }}>
      <Stack direction="row" sx={{ flex: 1, alignItems: "flex-end", gap: 1, pr: 2, pl: 4, pb: 1 }}>
        {/* Y-axis placeholder */}
        <Stack sx={{ width: 32, height: "100%", justifyContent: "space-between", pr: 1 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <SkeletonLayout
              // eslint-disable-next-line react/no-array-index-key
              key={`y-axis-${index}`}
              layout={{ type: "skeleton", variant: "text", width: "100%", typographyVariant: "subtitle1" }}
            />
          ))}
        </Stack>

        {/* Bars */}
        <Stack direction="row" sx={{ flex: 1, alignItems: "flex-end", justifyContent: "space-around", gap: 2 }}>
          {Array.from({ length: barCount }).map((_, index) => {
            const heightPercent = 30 + ((index * 17 + 13) % 50);
            return (
              <SkeletonLayout
                // eslint-disable-next-line react/no-array-index-key
                key={`bar-${index}`}
                layout={{
                  type: "skeleton",
                  variant: "rounded",
                  width: "100%",
                  height: `${(barHeight * heightPercent) / 100}px`,
                  sx: { borderRadius: "10px 10px 0 0", maxWidth: 60, flexShrink: 1 },
                }}
              />
            );
          })}
        </Stack>
      </Stack>

      {/* X-axis labels */}
      <Stack direction="row" sx={{ pl: "80px", pr: 2 }}>
        <Stack direction="row" sx={{ flex: 1, justifyContent: "space-around", gap: 2 }}>
          {Array.from({ length: barCount }).map((_, index) => (
            <SkeletonLayout
              // eslint-disable-next-line react/no-array-index-key
              key={`label-${index}`}
              layout={{
                type: "skeleton",
                variant: "text",
                width: "100%",
                typographyVariant: "subtitle1",
                sx: { maxWidth: 32, flexShrink: 1 },
              }}
            />
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
